import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import axios from "axios";

import * as XLSX from "xlsx";

import {
  FaArrowLeft,
  FaCalendarAlt,
  FaFileExcel,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";

import "./MonthlyDueStudentPage.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const DEFAULT_STUDENT_IMAGE =
  "https://dgps.co.in/public/admin/assets/img/default-stu.png";


const MonthlyDueStudentPage = () => {

  const {
    month,
    classId,
  } = useParams();


  const navigate =
    useNavigate();


  const location =
    useLocation();


  const [students, setStudents] =
    useState([]);


  const [monthName, setMonthName] =
    useState(

      location.state?.monthName ||
      month
    );


  const [className, setClassName] =
    useState(

      location.state?.className ||
      ""
    );


  const [search, setSearch] =
    useState("");


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  const [previewImage, setPreviewImage] =
    useState("");


  const [previewName, setPreviewName] =
    useState("");


  /* =========================================================
     HEADERS
  ========================================================= */

  const getHeaders = () => {

    const token =

      localStorage.getItem("token") ||

      localStorage.getItem(
        "auth_token"
      ) ||

      localStorage.getItem(
        "access_token"
      ) ||

      "";


    const schoolId =

      localStorage.getItem(
        "school_id"
      ) ||

      localStorage.getItem(
        "schoolId"
      ) ||

      localStorage.getItem(
        "selected_school_id"
      ) ||

      "";


    return {

      Authorization: token
        ? `Bearer ${token}`
        : "",

      "X-School-Id":
        schoolId,

      Accept:
        "application/json",
    };
  };


  /* =========================================================
     MONEY
  ========================================================= */

  const money = (amount) => {

    return new Intl.NumberFormat(
      "en-IN",
      {

        style:
          "currency",

        currency:
          "INR",

        minimumFractionDigits:
          0,

        maximumFractionDigits:
          2,
      }
    ).format(
      Number(amount || 0)
    );
  };


  /* =========================================================
     IMAGE
  ========================================================= */

  const getStudentImage = (
    student
  ) => {

    const file =

      student?.file ||

      student?.student_image ||

      "";


    if (!file) {

      return DEFAULT_STUDENT_IMAGE;
    }


    const cleanFile =

      String(file)
        .trim();


    if (
      cleanFile.startsWith(
        "https://"
      ) ||
      cleanFile.startsWith(
        "http://"
      )
    ) {

      return cleanFile;
    }


    if (
      cleanFile.startsWith(
        "/uploads/"
      )
    ) {

      return `${API_BASE_URL}${cleanFile}`;
    }


    if (
      cleanFile.startsWith(
        "uploads/"
      )
    ) {

      return `${API_BASE_URL}/${cleanFile}`;
    }


    return `${API_BASE_URL}/uploads/students/${cleanFile}`;
  };


  /* =========================================================
     LOAD STUDENTS
  ========================================================= */

  const loadStudents = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/fee-due/student-details`,

          {

            params: {

              month:
                month,

              class_id:
                classId,
            },

            headers:
              getHeaders(),
          }
        );


      if (
        response?.data?.status
      ) {

        const data =
          response.data.data ||
          {};


        const rows =

          Array.isArray(
            data.students
          )
            ? data.students
            : [];


        setStudents(
          rows
        );


        setMonthName(

          data.month_name ||

          location.state
            ?.monthName ||

          month
        );


        setClassName(

          data.class_name ||

          location.state
            ?.className ||

          rows[0]
            ?.class_name ||

          classId
        );

      } else {

        setStudents([]);


        setError(

          response?.data?.message ||

          "Unable to load student due report."
        );
      }

    } catch (err) {

      console.error(

        "Student due report error:",

        err
      );


      setStudents([]);


      setError(

        err?.response?.data
          ?.message ||

        "Unable to load student due report."
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    loadStudents();

  }, [
    month,
    classId,
  ]);


  /* =========================================================
     SEARCH
  ========================================================= */

  const filteredStudents =
    useMemo(() => {

      const q =

        search
          .trim()
          .toLowerCase();


      if (!q) {

        return students;
      }


      return students.filter(

        (student) => {

          const values = [

            student.name,

            student.student_code,

            student.mobile,

            student.roll_no,

            student.section_name,
          ];


          return values.some(

            (value) =>

              String(
                value ||
                ""
              )
                .toLowerCase()
                .includes(q)
          );
        }
      );

    }, [
      students,
      search,
    ]);


  /* =========================================================
     TOTAL
  ========================================================= */

  const totals =
    useMemo(() => {

      return filteredStudents.reduce(
        (
          result,
          student
        ) => {

          result.monthlyFee +=

            Number(
              student.monthly_fee ||
              0
            );


          result.transportFee +=

            Number(
              student.transport_fee ||
              0
            );


          result.monthlyPayable +=

            Number(
              student.monthly_payable ||
              0
            );


          result.collected +=

            Number(
              student.selected_month_paid ||
              0
            );


          result.monthDue +=

            Number(

              student.selected_month_due ??

              student.total_due ??

              0
            );


          return result;

        },
        {

          monthlyFee: 0,

          transportFee: 0,

          monthlyPayable: 0,

          collected: 0,

          monthDue: 0,
        }
      );

    }, [filteredStudents]);


  /* =========================================================
     EXCEL
  ========================================================= */

  const exportExcel = () => {

    if (!students.length) {

      return;
    }


    const rows =

      students.map(
        (
          student,
          index
        ) => ({

          "#":
            index + 1,

          "Student Name":
            student.name ||
            "",

          "Student Code":
            student.student_code ||
            "",

          "Mobile":
            student.mobile ||
            "",

          "Roll":
            student.roll_no ||
            "",

          "Section":
            student.section_name ||
            "",

          "Monthly Fee":

            Number(
              student.monthly_fee ||
              0
            ),

          "Transport":

            Number(
              student.transport_fee ||
              0
            ),

          "Payable":

            Number(
              student.monthly_payable ||
              0
            ),

          "Collected":

            Number(
              student.selected_month_paid ||
              0
            ),

          "Month Due":

            Number(

              student.selected_month_due ??

              student.total_due ??

              0
            ),
        })
      );


    const worksheet =

      XLSX.utils
        .json_to_sheet(
          rows
        );


    worksheet["!cols"] = [

      { wch: 6 },

      { wch: 25 },

      { wch: 16 },

      { wch: 16 },

      { wch: 10 },

      { wch: 12 },

      { wch: 16 },

      { wch: 14 },

      { wch: 14 },

      { wch: 14 },

      { wch: 14 },
    ];


    const workbook =

      XLSX.utils
        .book_new();


    XLSX.utils
      .book_append_sheet(

        workbook,

        worksheet,

        "Student Due"
      );


    XLSX.writeFile(

      workbook,

      `Student_Due_Class_${classId}_${month}.xlsx`
    );
  };


  /* =========================================================
     IMAGE PREVIEW
  ========================================================= */

  const openImagePreview = (
    image,
    name
  ) => {

    setPreviewImage(
      image
    );


    setPreviewName(

      name ||
      "Student"
    );
  };


  const closeImagePreview = () => {

    setPreviewImage("");

    setPreviewName("");
  };


  return (

    <div className="student-due-page">


      {/* BACK */}

      <button
        type="button"
        className="student-due-back"
        onClick={() =>
          navigate(-1)
        }
      >

        <FaArrowLeft />

        Class Report

      </button>


      {/* HEADER */}

      <div className="student-due-header">

        <div className="student-due-header-icon">

          <FaUserGraduate />

        </div>


        <div>

          <span>
            STUDENT MONTH DUE DETAILS
          </span>


          <h1>
            Class {className || classId}
          </h1>


          <p>

            Showing only{" "}

            <strong>
              {monthName}
            </strong>

            {" "}fee due.

          </p>

        </div>

      </div>


      {/* SUMMARY */}

      <div className="student-due-summary">


        <div className="student-summary-box">

          <div className="student-summary-icon blue">

            <FaUsers />

          </div>


          <div>

            <span>
              Due Students
            </span>

            <strong>

              {
                filteredStudents.length
              }

            </strong>

          </div>

        </div>


        <div className="student-summary-box">

          <div className="student-summary-icon purple">

            <FaCalendarAlt />

          </div>


          <div>

            <span>
              Selected Month
            </span>

            <strong>

              {monthName}

            </strong>

          </div>

        </div>


        <div className="student-summary-box">

          <div className="student-summary-icon red">

            <FaMoneyBillWave />

          </div>


          <div>

            <span>
              Month Outstanding
            </span>

            <strong className="summary-total-due">

              {money(
                totals.monthDue
              )}

            </strong>

          </div>

        </div>

      </div>


      {error && (

        <div className="student-due-error">

          {error}

        </div>

      )}


      {/* TABLE */}

      <div className="student-due-table-card">


        <div className="student-table-heading">


          <div>

            <h2>
              Student Due List
            </h2>

            <p>
              Student-wise {monthName}
              fee due details.
            </p>

          </div>


          <div
            style={{
              display:
                "flex",
              alignItems:
                "center",
              gap:
                "10px",
            }}
          >


            <div className="student-due-search">

              <FaSearch />


              <input
                type="text"
                placeholder="Search name, code, mobile, roll..."
                value={
                  search
                }
                onChange={(e) =>

                  setSearch(
                    e.target.value
                  )
                }
              />

            </div>


            <button
              type="button"
              style={excelButtonStyle}
              onClick={
                exportExcel
              }
              disabled={
                loading ||
                !students.length
              }
            >

              <FaFileExcel />

              Excel

            </button>

          </div>

        </div>


        {loading ? (

          <div className="student-due-loading">

            <div className="student-due-loader" />

            Loading student report...

          </div>

        ) : (

          <div className="student-due-table-wrap">


            <table className="student-due-table">


              <colgroup>

                <col style={{
                  width: "3%"
                }} />

                <col style={{
                  width: "5%"
                }} />

                <col style={{
                  width: "13%"
                }} />

                <col style={{
                  width: "9%"
                }} />

                <col style={{
                  width: "10%"
                }} />

                <col style={{
                  width: "5%"
                }} />

                <col style={{
                  width: "5%"
                }} />

                <col style={{
                  width: "10%"
                }} />

                <col style={{
                  width: "10%"
                }} />

                <col style={{
                  width: "10%"
                }} />

                <col style={{
                  width: "10%"
                }} />

                <col style={{
                  width: "10%"
                }} />

              </colgroup>


              <thead>

                <tr>

                  <th className="text-center">
                    #
                  </th>

                  <th className="text-center">
                    Photo
                  </th>

                  <th>
                    Student Name
                  </th>

                  <th>
                    Code
                  </th>

                  <th>
                    Mobile
                  </th>

                  <th className="text-center">
                    Roll
                  </th>

                  <th className="text-center">
                    Sec.
                  </th>

                  <th className="text-right">
                    M. Fee
                  </th>

                  <th className="text-right">
                    Transport
                  </th>

                  <th className="text-right">
                    Payable
                  </th>

                  <th className="text-right">
                    Collected
                  </th>

                  <th className="text-right">
                    Month Due
                  </th>

                </tr>

              </thead>


              <tbody>


                {!filteredStudents.length ? (

                  <tr>

                    <td
                      colSpan="12"
                      className="student-due-empty"
                    >

                      No due student found
                      for {monthName}.

                    </td>

                  </tr>

                ) : (

                  filteredStudents.map(
                    (
                      student,
                      index
                    ) => {


                      const image =

                        getStudentImage(
                          student
                        );


                      const selectedDue =

                        Number(

                          student.selected_month_due ??

                          student.total_due ??

                          0
                        );


                      return (

                        <tr
                          key={
                            student.student_id ||
                            index
                          }
                        >


                          <td className="text-center student-sl">

                            {index + 1}

                          </td>


                          {/* PHOTO */}

                          <td className="text-center">

                            <div
                              style={{
                                display:
                                  "flex",
                                justifyContent:
                                  "center",
                              }}
                            >

                              <div
                                className="student-table-photo student-photo-clickable"
                                onClick={() =>

                                  openImagePreview(
                                    image,
                                    student.name
                                  )
                                }
                              >

                                <img
                                  src={
                                    image
                                  }
                                  alt={
                                    student.name ||
                                    "Student"
                                  }
                                  onError={(e) => {

                                    e.currentTarget.onerror =
                                      null;

                                    e.currentTarget.src =
                                      DEFAULT_STUDENT_IMAGE;
                                  }}
                                />

                              </div>

                            </div>

                          </td>


                          {/* NAME */}

                          <td>

                            <strong className="student-name-text">

                              {
                                student.name ||
                                "-"
                              }

                            </strong>

                          </td>


                          {/* CODE */}

                          <td>

                            <span className="student-code-badge">

                              {
                                student.student_code ||
                                "-"
                              }

                            </span>

                          </td>


                          {/* MOBILE */}

                          <td>

                            {
                              student.mobile ||
                              "-"
                            }

                          </td>


                          {/* ROLL */}

                          <td className="text-center">

                            <span className="roll-number">

                              {
                                student.roll_no ||
                                "-"
                              }

                            </span>

                          </td>


                          {/* SECTION */}

                          <td className="text-center">

                            <span className="section-name">

                              {
                                student.section_name ||
                                "-"
                              }

                            </span>

                          </td>


                          {/* MONTHLY FEE */}

                          <td className="text-right">

                            {money(
                              student.monthly_fee
                            )}

                          </td>


                          {/* TRANSPORT */}

                          <td className="text-right">

                            {money(
                              student.transport_fee
                            )}

                          </td>


                          {/* PAYABLE */}

                          <td className="text-right">

                            <strong className="monthly-payable-value">

                              {money(
                                student.monthly_payable
                              )}

                            </strong>

                          </td>


                          {/* COLLECTED */}

                          <td className="text-right">

                            <strong
                              style={{
                                color:
                                  "#15803d",
                              }}
                            >

                              {money(
                                student.selected_month_paid
                              )}

                            </strong>

                          </td>


                          {/* DUE */}

                          <td className="text-right">

                            <strong className="student-total-due-value">

                              {money(
                                selectedDue
                              )}

                            </strong>

                          </td>


                        </tr>

                      );
                    }
                  )

                )}

              </tbody>


              {!!filteredStudents.length && (

                <tfoot>

                  <tr>


                    <td
                      colSpan="7"
                      className="grand-total-title"
                    >

                      GRAND TOTAL

                    </td>


                    <td className="text-right">

                      {money(
                        totals.monthlyFee
                      )}

                    </td>


                    <td className="text-right">

                      {money(
                        totals.transportFee
                      )}

                    </td>


                    <td className="text-right">

                      {money(
                        totals.monthlyPayable
                      )}

                    </td>


                    <td className="text-right">

                      {money(
                        totals.collected
                      )}

                    </td>


                    <td className="text-right grand-total-due">

                      {money(
                        totals.monthDue
                      )}

                    </td>


                  </tr>

                </tfoot>

              )}

            </table>

          </div>

        )}

      </div>


      {/* IMAGE PREVIEW */}

      {previewImage && (

        <div
          className="student-image-modal"
          onClick={
            closeImagePreview
          }
        >

          <div
            className="student-image-modal-content"
            onClick={(e) =>

              e.stopPropagation()
            }
          >

            <button
              type="button"
              className="student-image-close"
              onClick={
                closeImagePreview
              }
            >

              <FaTimes />

            </button>


            <div className="student-image-modal-title">

              <FaUserGraduate />

              <span>
                {previewName}
              </span>

            </div>


            <img
              src={
                previewImage
              }
              alt={
                previewName
              }
              className="student-large-image"
              onError={(e) => {

                e.currentTarget.onerror =
                  null;

                e.currentTarget.src =
                  DEFAULT_STUDENT_IMAGE;
              }}
            />

          </div>

        </div>

      )}

    </div>
  );
};


const excelButtonStyle = {

  height:
    "39px",

  border:
    "0",

  outline:
    "none",

  padding:
    "0 15px",

  borderRadius:
    "8px",

  background:
    "#15803d",

  color:
    "#ffffff",

  cursor:
    "pointer",

  fontSize:
    "11px",

  fontWeight:
    "800",

  display:
    "inline-flex",

  alignItems:
    "center",

  gap:
    "7px",

  whiteSpace:
    "nowrap",
};


export default MonthlyDueStudentPage;